import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import get from "lodash/get";
import { ResearchCard } from "../atoms/research-card";
import { TagRepresentationCard } from "../atoms/tag-representation-card";
import { Pagination } from "../atoms/Pagination";
import { useIsMobile } from "../hooks/useIsMobile";
import "./tag.m.css";
import LoadingIndicatorComponent from "../atoms/loading-indicator";
import { filterStoriesByTemplate } from "../../utils/story-utils";

const TagPage = (props) => {
  const config = useSelector((state) => get(state, ["qt", "config"], {}));
  const member = useSelector((state) => get(state, ["member"], null));
  const subscriberMetadata = useSelector((state) => get(state, ["subscriberMetadata"], null));
  const isAuthInitializing = useSelector((state) => get(state, ["isAuthInitializing"], false));
  // If user is logged in but subscriberMetadata is null, it's still loading
  const isMetadataLoading = member && subscriberMetadata === null;
  const { tag = {} } = props.data || {};

  //primary-role, secondary-role
  const primaryRole = get(subscriberMetadata, 'primary-role') || '';
  const secondaryRole = get(subscriberMetadata, 'secondary-role') || '';

  // Check if user is super-user (no forum filtering needed)
  const isSuperUser = primaryRole === 'super-user' || secondaryRole === 'super-user';

  // Build role query param only with valid roles, skip if super-user
  const roleQueryParam = (() => {
    if (isSuperUser) return null; // No forum filtering for super-user
    const roles = ['All-forums'];
    if (primaryRole) roles.push(primaryRole);
    if (secondaryRole) roles.push(secondaryRole);
    return roles.join(',');
  })();

  // State for managing fetched stories and pagination (already filtered by API)
  const [stories, setStories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  const [count, setCount] = useState(0);

  // Calculate stories to show on current page
  const storiesPerPage = 11;
  const currentPageStories = useMemo(() => {
    const startIndex = (currentPage - 1) * storiesPerPage;
    const endIndex = startIndex + storiesPerPage;
    return stories.slice(startIndex, endIndex);
  }, [stories, currentPage, storiesPerPage]);

  // Detect mobile screen size dynamically
  const isMobile = useIsMobile();

  // Effect to handle all story fetching (initial load and pagination)
  useEffect(() => {
    const fetchStories = async () => {
      if (isLoadingMore || isAuthInitializing || isMetadataLoading) return;
      const isInitialLoad = !hasInitialLoad;
      const offset = isInitialLoad ? 0 : stories.length;
      // Pagination guard
      if (!isInitialLoad && currentPage * storiesPerPage < stories.length) {
        return;
      }
      setIsLoadingMore(true);
      try {
        const limit = 11;
        const forumQuery = roleQueryParam
          ? `&story-attributes.forum=${roleQueryParam}`
          : '';
        const newTagSlug = decodeURIComponent(tag.slug).replace(/-/g, " ");
        const url = `/api/v1/advanced-search?tag-name-lower=${newTagSlug}&offset=${offset}&limit=${limit}${forumQuery}`;
  
        const response = await fetch(url);
        const data = await response.json();
  
        if (data?.items?.length) {
          setCount(data.total);

          // Extract stories from items and filter by template
          const stories = data.items.map(item => item.story || item);
          const filteredStories = filterStoriesByTemplate(stories);
          const filteredItems = filteredStories.map(story => ({ story }));

          setStories(prev =>
            isInitialLoad ? filteredItems : [...prev, ...filteredItems]
          );
        }
  
        if (isInitialLoad) {
          setHasInitialLoad(true);
        }
      } catch (error) {
        console.error(
          isInitialLoad
            ? 'Error fetching initial stories:'
            : 'Error fetching more stories:',
          error
        );
  
        if (isInitialLoad) {
          setHasInitialLoad(true);
        }
      } finally {
        setIsLoadingMore(false);
      }
    };
  
    fetchStories();
  }, [
    isAuthInitializing, isMetadataLoading, roleQueryParam, tag.slug, currentPage, hasInitialLoad, stories.length
  ]);
  

  useEffect(() => {
    // Handle browser back/forward navigation
    const handlePopState = (event) => {
      const page = event.state?.page || 1;
      if (page !== currentPage) {
        setCurrentPage(page);
      }
    };

    window.addEventListener('popstate', handlePopState);

    // Cleanup
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [currentPage]);

  // Get the primary brand color from config
  const brandColor = get(config, ["publisher-attributes", "theme_color"], "#FF2626");

  // Extract stories from the data structure
  const storyItems = currentPageStories.map(item => item.story || item);

  const totalCount = count;

  // Calculate story type for display (report/article)
  const getStoryType = () => {
    if (totalCount === 1) return "Report";
    return "Reports";
  };

  // Calculate total pages based on filtered stories
  const totalPages = Math.ceil(totalCount / storiesPerPage);

  // Handle page change for pagination
  const handlePageChange = (page) => {
    if (page !== currentPage) {
      setCurrentPage(page); // The useEffect will handle fetching more stories

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      // Update URL without page refresh
      const newUrl = `${tag.path}${page > 1 ? `?page=${page}` : ''}`;
      window.history.pushState({ page }, '', newUrl);
    }
  };

  // Show loading while auth is initializing or waiting for subscriber metadata for logged-in users
  if (isAuthInitializing || (member && !subscriberMetadata)) {
    return <LoadingIndicatorComponent />;
  }

  return (
    <div styleName="tag-page">
      <div styleName="tag-page-container">
        {/* Page Title with Count - Desktop and Tablet only */}
        {!isMobile && (
          <div styleName="tag-page-header">
            <div styleName="tag-page-title-wrapper">
              <p styleName="tag-page-title">{totalCount} {getStoryType()}</p>
            </div>
          </div>
        )}

        {/* Story Grid */}
        <div styleName="tag-story-grid">
          {/* First Card - Tag Representation Card */}
          <div styleName="tag-story-item">
            <TagRepresentationCard
              tagName={tag.name}
              tagDescription={tag.description}
              brandColor={brandColor}
              isMobile={isMobile}
            />
          </div>

          {/* Page Title with Count - Mobile only (after tag card) */}
          {isMobile && (
            <div styleName="tag-page-header-mobile">
              <div styleName="tag-page-title-wrapper">
                <p styleName="tag-page-title">{totalCount} {getStoryType()}</p>
              </div>
            </div>
          )}

          {/* Story Cards - All stories from the data */}
          {storyItems.map((story, index) => (
            <div key={story.id || index} styleName="tag-story-item">
              <ResearchCard
                story={story}
                position={index}
                backgroundColor={null}
                showBottomBorder={true}
                hideImage={isMobile}
              />
            </div>
          ))}
        </div>

        {/* Pagination */}
        <Pagination
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

TagPage.propTypes = {
  data: PropTypes.shape({
    stories: PropTypes.array,
    tag: PropTypes.shape({
      slug: PropTypes.string,
      name: PropTypes.string,
      description: PropTypes.string,
      total: PropTypes.number,
      path: PropTypes.string,
    }),
    offset: PropTypes.number,
    limit: PropTypes.number,
  }),
};

export { TagPage };
